import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
	ArrowUpRight,
	Check,
	CreditCard,
	Info,
	Loader2,
	Minus,
	TriangleAlert,
	UsersRound
} from "lucide-react"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { Button, Card, SectionHeading, Spinner, cx } from "../../components/ui"
import { countLabel, fileSize, fullTimestamp, limitLabel } from "../../lib/format"

/**
 * Plans and checkout.
 *
 * The upgrade button hands off to Stripe Checkout - no card field ever exists
 * in this bundle, which is the whole reason for using hosted checkout. With no
 * Stripe key configured the page still renders every plan and says plainly that
 * payment is not wired up yet, rather than pretending to take money.
 */
export function BillingSettings() {
	const { user, refreshUser, toast } = useApp()
	const [params, setParams] = useSearchParams()
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(true)
	const [interval, setInterval] = useState("monthly")
	const [busyPlan, setBusyPlan] = useState(null)

	const checkoutResult = params.get("checkout")
	const upsell = params.get("upsell")

	useEffect(() => {
		api
			.billing()
			.then(setData)
			.catch((cause) => toast(cause.message, "error"))
			.finally(() => setLoading(false))
	}, [toast])

	// Coming back from Stripe: the webhook is the source of truth, so refresh
	// rather than trusting the redirect.
	useEffect(() => {
		if (!checkoutResult) return
		if (checkoutResult === "success") {
			toast("Payment received — updating your plan", "success")
			refreshUser()
			api.billing().then(setData).catch(() => {})
		}
		const next = new URLSearchParams(params)
		next.delete("checkout")
		next.delete("session_id")
		setParams(next, { replace: true })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checkoutResult])

	async function upgrade(planKey) {
		setBusyPlan(planKey)
		try {
			const session = await api.checkout(planKey, interval)
			if (session.url) {
				window.location.assign(session.url)
				return
			}
			toast(
				session.reason === "price_missing"
					? "That plan has no Stripe price configured yet."
					: "Stripe is not configured yet, so checkout is in preview mode.",
				"info"
			)
		} catch (cause) {
			toast(cause.message, "error")
		} finally {
			setBusyPlan(null)
		}
	}

	async function openPortal() {
		setBusyPlan("portal")
		try {
			const session = await api.billingPortal()
			if (session.url) window.location.assign(session.url)
			else toast("There is no subscription to manage yet.", "info")
		} catch (cause) {
			toast(cause.message, "error")
		} finally {
			setBusyPlan(null)
		}
	}

	if (loading) return <Spinner label="Loading plans" />
	if (!data) return null

	const billing = data.billing || {}
	const usage = user?.usage || {}

	return (
		<div className="space-y-5">
			{upsell === "teams" && (
				<Banner tone="info" icon={UsersRound}>
					<strong className="font-semibold">Teams</strong> are part of Plus and Max.
					Save a group of people once and invite the whole group to an issue in one
					click.
				</Banner>
			)}
			{checkoutResult === "cancelled" && (
				<Banner tone="info" icon={Info}>
					Checkout was cancelled. Nothing has been charged.
				</Banner>
			)}
			{billing.status === "past_due" && (
				<Banner tone="warning" icon={TriangleAlert}>
					Your last payment failed. Update your card to keep your plan.
				</Banner>
			)}
			{!data.live && (
				<Banner tone="info" icon={Info}>
					Stripe is not configured, so this page is in preview mode. Add
					<code className="mx-1 rounded bg-black/5 px-1 font-mono text-2xs">
						STRIPE_SECRET_KEY
					</code>
					and the price IDs to take real payments.
				</Banner>
			)}

			<Card className="p-5">
				<SectionHeading
					title="Your plan"
					description="What you are on now, and what you have used."
					className="mb-4"
					action={
						billing.subscription_id ? (
							<Button
								size="sm"
								icon={CreditCard}
								onClick={openPortal}
								busy={busyPlan === "portal"}
							>
								Manage billing
							</Button>
						) : null
					}
				/>

				<div className="flex flex-wrap items-center gap-x-6 gap-y-3">
					<div>
						<p className="text-2xl font-semibold leading-none text-ink-900">
							{user?.plan?.name}
						</p>
						<p className="mt-1 text-2xs text-ink-500">
							{billing.status === "active" && billing.current_period_end
								? `Renews ${fullTimestamp(billing.current_period_end, user?.timezone)}`
								: billing.cancel_at_period_end
									? "Cancels at the end of this period"
									: "No subscription"}
						</p>
					</div>

					<div className="min-w-[12rem] flex-1">
						<div className="flex items-baseline justify-between text-2xs text-ink-500">
							<span>Attachment storage</span>
							<span className="tabular">
								{fileSize(usage.bytes_used)} of {fileSize(usage.bytes_limit)}
							</span>
						</div>
						<div className="mt-1.5 h-2 overflow-hidden rounded-full bg-ink-100">
							<div
								className={cx(
									"h-full rounded-full",
									usage.percent_used >= 80 ? "bg-state-open" : "bg-brand-500"
								)}
								style={{ width: `${Math.max(2, Math.min(100, usage.percent_used || 0))}%` }}
							/>
						</div>
					</div>
				</div>
			</Card>

			<div>
				<div className="mb-3 flex flex-wrap items-end justify-between gap-3">
					<SectionHeading
						title="Plans"
						description="Change or cancel at any time. Nothing is deleted if you downgrade."
					/>
					<IntervalToggle value={interval} onChange={setInterval} />
				</div>

				<div className="grid gap-3 lg:grid-cols-3">
					{data.plans.map((plan) => (
						<PlanCard
							key={plan.key}
							plan={plan}
							interval={interval}
							currencySymbol={data.currency_symbol || "$"}
							busy={busyPlan === plan.key}
							onUpgrade={() => upgrade(plan.key)}
						/>
					))}
				</div>

				<p className="mt-3 text-2xs leading-relaxed text-ink-500">
					Prices are per person, per month, in{" "}
					{(data.currency || "usd").toUpperCase()}, excluding tax
					{data.settlement_currency &&
						data.settlement_currency !== data.currency &&
						`, settled in ${data.settlement_currency.toUpperCase()} by Stripe`}
					. Only the person who owns a project needs a paid plan — people you invite to
					an issue do not.
				</p>
			</div>
		</div>
	)
}

/**
 * Prices come from the server, never from a constant here.
 *
 * The yearly figure is the whole year, so the headline shows what it works out
 * at per month - which is the number people compare - with the annual total
 * underneath.
 */
function money(symbol, amount) {
	if (!amount) return `${symbol}0`
	const rounded = Math.round(amount * 100) / 100
	return `${symbol}${rounded % 1 === 0 ? rounded : rounded.toFixed(2)}`
}

function PlanCard({ plan, interval, currencySymbol, busy, onUpgrade }) {
	const perMonth =
		interval === "yearly" ? (plan.price_yearly || 0) / 12 : plan.price_monthly || 0
	const headline = money(currencySymbol, perMonth)
	const note =
		plan.key === "free"
			? "Free forever"
			: interval === "yearly"
				? `${money(currencySymbol, plan.price_yearly)} billed yearly`
				: "Billed monthly"
	const isCurrent = plan.is_current
	const highlight = plan.key === "plus"

	const features = [
		[limitLabel(plan.projects), "projects"],
		[limitLabel(plan.issues_per_project), "issues per project"],
		[fileSize(plan.storage_bytes), "attachment storage"],
		[fileSize(plan.max_upload_bytes), "max per file"],
		[limitLabel(plan.members_per_issue), "people per issue"]
	]

	return (
		<div
			className={cx(
				"relative flex flex-col rounded-card border bg-white p-4 shadow-card",
				isCurrent
					? "border-brand-400 ring-1 ring-brand-300"
					: highlight
						? "border-ink-300"
						: "border-ink-200/80"
			)}
		>
			{highlight && !isCurrent && (
				<span className="absolute -top-2 left-4 rounded-full bg-brand-700 px-2 py-0.5 text-2xs font-semibold text-white">
					Most teams
				</span>
			)}
			{isCurrent && (
				<span className="absolute -top-2 left-4 rounded-full bg-ink-900 px-2 py-0.5 text-2xs font-semibold text-white">
					Current plan
				</span>
			)}

			<h3 className="text-[0.9375rem] font-semibold text-ink-900">{plan.name}</h3>
			<p className="mt-1.5 flex items-baseline gap-1">
				<span className="text-2xl font-semibold tracking-tight text-ink-900">
					{headline}
				</span>
				{plan.key !== "free" && (
					<span className="text-2xs text-ink-500">/ user / month</span>
				)}
			</p>
			<p className="mt-0.5 text-2xs text-ink-500">{note}</p>

			<ul className="mt-4 flex-1 space-y-1.5">
				{features.map(([value, label]) => (
					<li key={label} className="flex items-start gap-2 text-[0.8125rem] text-ink-700">
						<Check size={13} className="mt-0.5 shrink-0 text-state-closed" aria-hidden />
						<span>
							<strong className="font-semibold text-ink-900">{value}</strong> {label}
						</span>
					</li>
				))}
				<li className="flex items-start gap-2 text-[0.8125rem] text-ink-700">
					{plan.teams === 0 ? (
						<Minus size={13} className="mt-0.5 shrink-0 text-ink-300" aria-hidden />
					) : (
						<Check size={13} className="mt-0.5 shrink-0 text-state-closed" aria-hidden />
					)}
					<span className={cx(plan.teams === 0 && "text-ink-400")}>
						{countLabel(plan.teams, "team")}
					</span>
				</li>
				<li className="flex items-start gap-2 text-[0.8125rem] text-ink-700">
					{plan.digest ? (
						<Check size={13} className="mt-0.5 shrink-0 text-state-closed" aria-hidden />
					) : (
						<Minus size={13} className="mt-0.5 shrink-0 text-ink-300" aria-hidden />
					)}
					<span className={cx(!plan.digest && "text-ink-400")}>Daily summary email</span>
				</li>
			</ul>

			<div className="mt-4">
				{isCurrent ? (
					<Button className="w-full" disabled>
						Your plan
					</Button>
				) : plan.purchasable ? (
					<Button
						className="w-full"
						variant={highlight ? "primary" : "secondary"}
						iconRight={busy ? undefined : ArrowUpRight}
						onClick={onUpgrade}
						busy={busy}
						disabled={busy}
					>
						{busy ? "Opening Stripe" : `Upgrade to ${plan.name}`}
					</Button>
				) : (
					<Button className="w-full" disabled>
						Downgrade in billing portal
					</Button>
				)}
			</div>
		</div>
	)
}

function IntervalToggle({ value, onChange }) {
	return (
		<div className="inline-flex rounded-lg bg-ink-100 p-0.5">
			{[
				["monthly", "Monthly"],
				["yearly", "Yearly"]
			].map(([key, label]) => (
				<button
					key={key}
					type="button"
					onClick={() => onChange(key)}
					className={cx(
						"rounded-md px-3 py-1 text-2xs font-medium transition-colors",
						value === key ? "bg-white text-ink-900 shadow-card" : "text-ink-600"
					)}
				>
					{label}
					{key === "yearly" && (
						<span className="ml-1.5 text-state-closed">2 months free</span>
					)}
				</button>
			))}
		</div>
	)
}

function Banner({ tone, icon: Icon, children }) {
	return (
		<div
			className={cx(
				"flex items-start gap-2.5 rounded-lg px-3.5 py-2.5 text-[0.8125rem] leading-relaxed",
				tone === "warning"
					? "bg-state-open-soft text-state-open-ink ring-1 ring-inset ring-state-open/25"
					: "bg-brand-50 text-brand-900"
			)}
		>
			<Icon size={15} className="mt-0.5 shrink-0" aria-hidden />
			<span>{children}</span>
		</div>
	)
}
